/**
 * PatchMatrix — the Studio's single 12-row grid.
 *
 * One voice is one row, and that row runs the full width of the page: its
 * parameters on the left, its sequencer lane on the right, its level and
 * meter at the seam. Both halves are rendered here rather than as two
 * independent widgets because they must stay locked to the same row rhythm —
 * hover, focus, mute and solo all read across the whole row.
 *
 * The component owns presentation only. Values live in the caller's state and
 * arrive back through the `on*` callbacks.
 */

import {
  ENGINE_TYPES,
  ENGINE_SHORT_NAMES,
  ENGINE_DISPLAY_NAMES,
  VOICE_HUES,
  PARAM_GROUPS,
  getGroupedParams,
  normalize,
  denormalize,
  formatValue,
  gainToDb,
} from '../engine-data.js';

const STEPS_PER_BAR = 4;
const MAX_STEPS = 32;

/** Fill height of a step cell per velocity level (0 = off). */
const VELOCITY_HEIGHTS = ['0%', '38%', '66%', '100%'];

/** Opacity of a step's fill per velocity level. */
const VELOCITY_ALPHA = [0, 0.55, 0.78, 1];

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Two-digit voice number, matching the design's 01…12 gutter. */
function padIndex(i) {
  return String(i + 1).padStart(2, '0');
}

export class PatchMatrix {
  constructor(options = {}) {
    this.engineTypes = options.engineTypes || ENGINE_TYPES;
    this.getParamValue = options.getParamValue || (() => 0);
    this.getStep = options.getStep || (() => ({ velocity: 0, accent: false }));
    this.getLevel = options.getLevel || (() => 0.8);

    this.onParamChange = options.onParamChange || (() => {});
    this.onStepChange = options.onStepChange || (() => {});
    this.onLevelChange = options.onLevelChange || (() => {});
    this.onMuteChange = options.onMuteChange || (() => {});
    this.onSoloChange = options.onSoloChange || (() => {});
    this.onAudition = options.onAudition || (() => {});
    this.onFocusChange = options.onFocusChange || (() => {});
    this.onGestureStart = options.onGestureStart || (() => {});
    this.onExpand = options.onExpand || (() => {});

    this.stepCount = options.stepCount || 16;
    this.focusedEngine = null;
    this.currentStep = -1;

    /** Per-voice DOM handles, keyed by engine type. */
    this.rows = new Map();

    this.paramsPanel = this.buildParamsPanel();
    this.stepsPanel = this.buildStepsPanel();

    this.engineTypes.forEach((engineType, index) => {
      this.buildRow(engineType, index);
    });

    // A velocity paint ends wherever the pointer is released, which is often
    // not over the cell it started on — so the release is watched globally
    // rather than per-button, otherwise the paint would stay armed.
    this.endPaint = () => {
      this.paintingLane = null;
      this.paintVelocity = undefined;
    };
    window.addEventListener('pointerup', this.endPaint);
    window.addEventListener('pointercancel', this.endPaint);

    this.syncAll();
  }

  /* ---------------------------------------------------------------- *
   * Panels
   * ---------------------------------------------------------------- */

  buildParamsPanel() {
    const panel = el('div', 'pm-params');
    panel.id = 'params-panel';

    // Inner wrapper so the panel itself can become a horizontal scroll port
    // at narrow widths while every row inside it still shares one column
    // grid. Twelve independently scrolling rows would desynchronise, and a
    // matrix whose rows do not line up is not a matrix.
    const inner = el('div', 'pm-params-inner');
    panel.appendChild(inner);

    const head = el('div', 'pm-head');
    head.appendChild(el('div', 'pm-head-voice', 'VOICE'));

    const groups = el('div', 'pm-head-groups');
    PARAM_GROUPS.forEach((name, i) => {
      const group = el('div', 'pm-head-group');
      group.dataset.group = String(i);
      group.appendChild(el('span', null, name));
      group.appendChild(el('i', 'pm-head-rule'));
      groups.appendChild(group);
    });
    head.appendChild(groups);
    head.appendChild(el('div', 'pm-head-level', 'LEVEL'));

    inner.appendChild(head);

    this.paramRows = el('div', 'pm-rows');
    inner.appendChild(this.paramRows);

    return panel;
  }

  buildStepsPanel() {
    const panel = el('div', 'pm-steps');
    panel.id = 'steps-panel';

    // Same reason as the params panel — and here the inner wrapper is also
    // what the playhead is positioned against, so the overlay travels with
    // the lanes when the panel scrolls instead of drifting off them.
    const inner = el('div', 'pm-steps-inner');
    panel.appendChild(inner);

    this.stepHead = el('div', 'pm-step-head');
    inner.appendChild(this.stepHead);

    this.stepRows = el('div', 'pm-step-rows');
    inner.appendChild(this.stepRows);

    // The playhead is an overlay rather than a class on the live cells:
    // repainting one absolutely-positioned column each step is far cheaper
    // than touching 12 buttons, and it keeps the wash *above* the velocity
    // fills so a lit column reads at any density.
    this.playhead = el('div', 'pm-playhead');
    this.playhead.setAttribute('aria-hidden', 'true');
    inner.appendChild(this.playhead);

    this.buildStepHeader();

    return panel;
  }

  /** Bar-grouped step numbers, plus the matching playhead columns. */
  buildStepHeader() {
    this.stepHead.replaceChildren();
    this.playhead.replaceChildren();
    this.playheadCells = [];

    const barCount = Math.ceil(this.stepCount / STEPS_PER_BAR);

    for (let bar = 0; bar < barCount; bar++) {
      const headBar = el('div', 'pm-bar');
      const playBar = el('div', 'pm-bar');

      for (let i = 0; i < STEPS_PER_BAR; i++) {
        const index = bar * STEPS_PER_BAR + i;

        const num = el('div', 'pm-step-num', String(index + 1));
        if (i === 0) num.classList.add('is-downbeat');
        headBar.appendChild(num);

        const cell = el('div', 'pm-playhead-cell');
        playBar.appendChild(cell);
        this.playheadCells.push(cell);
      }

      this.stepHead.appendChild(headBar);
      this.playhead.appendChild(playBar);
    }
  }

  /* ---------------------------------------------------------------- *
   * Rows
   * ---------------------------------------------------------------- */

  buildRow(engineType, index) {
    const hue = VOICE_HUES[engineType] || '#35e08d';
    const shortName = ENGINE_SHORT_NAMES[engineType] || engineType.slice(0, 4).toUpperCase();
    const fullName = ENGINE_DISPLAY_NAMES[engineType] || engineType;

    /* ---- left half: identity, parameters, level ---- */

    const paramRow = el('div', 'pm-row');
    paramRow.dataset.engine = engineType;
    paramRow.style.setProperty('--hue', hue);

    paramRow.appendChild(el('div', 'pm-tick'));

    const ms = el('div', 'pm-ms');
    const muteBtn = el('button', 'pm-mute', 'M');
    muteBtn.type = 'button';
    muteBtn.dataset.engine = engineType;
    muteBtn.setAttribute('aria-pressed', 'false');
    muteBtn.setAttribute('aria-label', `Mute ${fullName}`);
    muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onMuteChange(engineType, muteBtn.getAttribute('aria-pressed') !== 'true');
    });

    const soloBtn = el('button', 'pm-solo', 'S');
    soloBtn.type = 'button';
    soloBtn.dataset.engine = engineType;
    soloBtn.setAttribute('aria-pressed', 'false');
    soloBtn.setAttribute('aria-label', `Solo ${fullName}`);
    soloBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onSoloChange(engineType, soloBtn.getAttribute('aria-pressed') !== 'true');
    });

    ms.append(muteBtn, soloBtn);
    paramRow.appendChild(ms);

    paramRow.appendChild(el('div', 'pm-index', padIndex(index)));

    const nameBtn = el('button', 'pm-name', shortName);
    nameBtn.type = 'button';
    nameBtn.dataset.engine = engineType;
    nameBtn.title = `${fullName} — click to audition`;
    nameBtn.setAttribute('aria-label', `Audition ${fullName}`);
    nameBtn.addEventListener('click', () => {
      this.setFocus(engineType);
      this.onAudition(engineType);
    });
    paramRow.appendChild(nameBtn);

    // The matrix shows the nine parameters that shape a voice; an engine has
    // many more. This opens the full set without disturbing the resting grid.
    const expandBtn = el('button', 'pm-expand', '⋯');
    expandBtn.type = 'button';
    expandBtn.dataset.action = 'expand';
    expandBtn.dataset.engine = engineType;
    expandBtn.title = `All ${fullName} parameters`;
    expandBtn.setAttribute('aria-label', `Open all ${fullName} parameters`);
    expandBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.setFocus(engineType);
      this.onExpand(engineType);
    });
    paramRow.appendChild(expandBtn);

    const cells = el('div', 'pm-cells');
    const paramCells = new Map();
    getGroupedParams(engineType).forEach((param) => {
      const cell = this.buildParamCell(engineType, param);
      paramCells.set(param.name, cell);
      cells.appendChild(cell.element);
    });
    paramRow.appendChild(cells);

    const level = this.buildLevel(engineType, fullName);
    paramRow.appendChild(level.element);

    paramRow.addEventListener('pointerdown', () => this.setFocus(engineType));

    /* ---- right half: the lane ---- */

    const lane = el('div', 'pm-lane');
    lane.dataset.engine = engineType;
    lane.style.setProperty('--hue', hue);
    lane.addEventListener('pointerdown', () => this.setFocus(engineType));

    const stepCells = [];
    this.buildLaneSteps(engineType, lane, stepCells);

    this.paramRows.appendChild(paramRow);
    this.stepRows.appendChild(lane);

    this.rows.set(engineType, {
      index,
      hue,
      paramRow,
      lane,
      muteBtn,
      soloBtn,
      paramCells,
      level,
      stepCells,
    });
  }

  /** One step-cell grid for a lane, grouped into bars of four. */
  buildLaneSteps(engineType, lane, stepCells) {
    lane.replaceChildren();
    stepCells.length = 0;

    const barCount = Math.ceil(this.stepCount / STEPS_PER_BAR);

    for (let bar = 0; bar < barCount; bar++) {
      const barEl = el('div', 'pm-bar');

      for (let i = 0; i < STEPS_PER_BAR; i++) {
        const index = bar * STEPS_PER_BAR + i;
        const btn = el('button', 'pm-step');
        btn.type = 'button';
        btn.dataset.step = String(index);
        btn.dataset.engine = engineType;
        btn.setAttribute('aria-label', `Step ${index + 1}`);

        const fill = el('i', 'pm-step-fill');
        btn.appendChild(fill);

        this.bindStep(engineType, index, btn);

        barEl.appendChild(btn);
        stepCells.push({ button: btn, fill });
      }

      lane.appendChild(barEl);
    }
  }

  /* ---------------------------------------------------------------- *
   * Parameter cells
   * ---------------------------------------------------------------- */

  buildParamCell(engineType, param) {
    const button = el('button', 'pm-cell');
    button.type = 'button';
    button.dataset.param = param.name;
    button.dataset.engine = engineType;
    button.dataset.group = String(param.group);
    if (param.isGroupEnd) button.classList.add('is-group-end');
    button.title = `${param.label} — drag to adjust, double-click to reset`;

    const label = el('span', 'pm-cell-label', param.label.toUpperCase());
    const value = el('span', 'pm-cell-value');
    const digits = el('b', null, '0');
    const unit = el('i', null, '');
    value.append(digits, unit);

    const track = el('span', 'pm-cell-bar');
    const bar = el('i');
    track.appendChild(bar);

    button.append(label, value, track);

    const cell = { element: button, param, digits, unit, bar };
    this.bindParamDrag(engineType, param, cell);

    return cell;
  }

  /**
   * Vertical drag adjusts the value; the cell behaves like a micro-fader.
   * Holding Alt scales the gesture down for fine work — the footer advertises
   * it, so it has to be real.
   */
  bindParamDrag(engineType, param, cell) {
    const button = cell.element;
    let dragging = false;
    let startY = 0;
    let startNorm = 0;
    let moved = false;

    const FULL_RANGE_PX = 140;

    button.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      moved = false;
      startY = e.clientY;
      startNorm = normalize(param, this.getParamValue(engineType, param.name));
      button.setPointerCapture(e.pointerId);
      button.classList.add('is-active');
      this.onGestureStart('param');
      e.preventDefault();
    });

    button.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const delta = startY - e.clientY;
      if (Math.abs(delta) > 2) moved = true;
      const scale = e.altKey ? 0.25 : 1;
      const next = startNorm + (delta / FULL_RANGE_PX) * scale;
      this.onParamChange(engineType, param.name, denormalize(param, next));
    });

    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      button.classList.remove('is-active');
      if (button.hasPointerCapture?.(e.pointerId)) {
        button.releasePointerCapture(e.pointerId);
      }
      // A tap that never moved auditions the voice, so you can hear what the
      // parameter you just reached for actually does.
      if (!moved) this.onAudition(engineType);
    };

    button.addEventListener('pointerup', end);
    button.addEventListener('pointercancel', end);

    button.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.onGestureStart('param');
      this.onParamChange(engineType, param.name, param.default);
    });

    button.addEventListener('keydown', (e) => {
      const coarse = e.shiftKey ? 0.1 : 0.02;
      let direction = 0;
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') direction = 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') direction = -1;
      else return;

      e.preventDefault();
      this.onGestureStart('param');
      const current = normalize(param, this.getParamValue(engineType, param.name));
      this.onParamChange(engineType, param.name, denormalize(param, current + direction * coarse));
    });
  }

  /* ---------------------------------------------------------------- *
   * Level + meter
   * ---------------------------------------------------------------- */

  buildLevel(engineType, fullName) {
    const wrap = el('div', 'pm-level');

    const track = el('div', 'pm-fader');
    track.setAttribute('role', 'slider');
    track.setAttribute('tabindex', '0');
    track.setAttribute('aria-label', `${fullName} level`);
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    const fill = el('i', 'pm-fader-fill');
    const thumb = el('i', 'pm-fader-thumb');
    track.append(fill, thumb);

    const meter = el('div', 'pm-meter');
    const meterFill = el('i');
    meter.appendChild(meterFill);

    const db = el('div', 'pm-db', '0.0');

    wrap.append(track, meter, db);

    let dragging = false;

    const apply = (clientX) => {
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const value = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      this.onLevelChange(engineType, value);
    };

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      track.setPointerCapture(e.pointerId);
      this.onGestureStart('level');
      apply(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    });
    track.addEventListener('pointermove', (e) => {
      if (dragging) apply(e.clientX);
    });
    const stop = (e) => {
      if (!dragging) return;
      dragging = false;
      if (track.hasPointerCapture?.(e.pointerId)) track.releasePointerCapture(e.pointerId);
    };
    track.addEventListener('pointerup', stop);
    track.addEventListener('pointercancel', stop);

    track.addEventListener('keydown', (e) => {
      let direction = 0;
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') direction = 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') direction = -1;
      else return;
      e.preventDefault();
      this.onGestureStart('level');
      const step = e.shiftKey ? 0.1 : 0.02;
      this.onLevelChange(engineType, this.getLevel(engineType) + direction * step);
    });

    return { element: wrap, track, fill, thumb, meterFill, db };
  }

  /* ---------------------------------------------------------------- *
   * Steps
   * ---------------------------------------------------------------- */

  bindStep(engineType, index, button) {
    let paintVelocity = 0;

    button.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this.onGestureStart('step');

      const current = this.getStep(engineType, index);

      if (e.altKey) {
        // Alt toggles accent without disturbing the velocity you dialled in.
        this.onStepChange(engineType, index, {
          velocity: current.velocity,
          accent: !current.accent,
        });
        e.preventDefault();
        return;
      }

      if (e.shiftKey) {
        // Shift starts a velocity paint: the level is picked from where in
        // the cell you grabbed it, then dragged across the lane.
        paintVelocity = this.velocityFromPoint(button, e.clientY);
      } else {
        paintVelocity = (current.velocity + 1) % 4;
      }

      this.paintVelocity = paintVelocity;
      this.paintingLane = engineType;
      this.onStepChange(engineType, index, { velocity: paintVelocity, accent: current.accent });
      e.preventDefault();
    });

    // Dragging across the lane with the button held stamps the same velocity
    // onto every step it crosses — the fastest way to lay down a hat run.
    button.addEventListener('pointerenter', () => {
      if (this.paintingLane !== engineType || this.paintVelocity === undefined) return;
      const current = this.getStep(engineType, index);
      this.onStepChange(engineType, index, {
        velocity: this.paintVelocity,
        accent: current.accent,
      });
    });

    button.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.onGestureStart('step');
      this.onStepChange(engineType, index, { velocity: 0, accent: false });
    });
  }

  /** Velocity 1–3 from where inside a cell the pointer landed. */
  velocityFromPoint(button, clientY) {
    const rect = button.getBoundingClientRect();
    if (!rect.height) return 3;
    const fromBottom = 1 - (clientY - rect.top) / rect.height;
    if (fromBottom < 0.34) return 1;
    if (fromBottom < 0.7) return 2;
    return 3;
  }

  /* ---------------------------------------------------------------- *
   * Sync — pull current values out of state and onto the DOM
   * ---------------------------------------------------------------- */

  syncAll() {
    this.engineTypes.forEach((engineType) => {
      this.syncParams(engineType);
      this.syncSteps(engineType);
      this.syncLevel(engineType);
    });
  }

  syncParams(engineType) {
    const row = this.rows.get(engineType);
    if (!row) return;
    row.paramCells.forEach((cell) => this.paintParamCell(engineType, cell));
  }

  syncParam(engineType, paramName) {
    const cell = this.rows.get(engineType)?.paramCells.get(paramName);
    if (cell) this.paintParamCell(engineType, cell);
  }

  paintParamCell(engineType, cell) {
    const value = this.getParamValue(engineType, cell.param.name);
    if (value === undefined) return;
    const { text, unit } = formatValue(cell.param, value);
    cell.digits.textContent = text;
    cell.unit.textContent = unit;
    cell.bar.style.width = `${normalize(cell.param, value) * 100}%`;
  }

  syncSteps(engineType) {
    const row = this.rows.get(engineType);
    if (!row) return;
    row.stepCells.forEach((cell, index) => this.paintStepCell(engineType, index, cell));
  }

  syncStep(engineType, index) {
    const cell = this.rows.get(engineType)?.stepCells[index];
    if (cell) this.paintStepCell(engineType, index, cell);
  }

  paintStepCell(engineType, index, cell) {
    const step = this.getStep(engineType, index) || { velocity: 0, accent: false };
    const velocity = Math.max(0, Math.min(3, step.velocity | 0));

    cell.button.dataset.velocity = String(velocity);
    cell.button.classList.toggle('is-accent', !!step.accent && velocity > 0);
    cell.fill.style.height = VELOCITY_HEIGHTS[velocity];
    cell.fill.style.opacity = String(VELOCITY_ALPHA[velocity]);
  }

  syncLevel(engineType) {
    const row = this.rows.get(engineType);
    if (!row) return;

    const value = Math.min(1, Math.max(0, this.getLevel(engineType)));
    const percent = `${value * 100}%`;
    row.level.fill.style.width = percent;
    row.level.thumb.style.left = percent;
    row.level.db.textContent = gainToDb(value);
    row.level.track.setAttribute('aria-valuenow', String(Math.round(value * 100)));
    row.level.track.setAttribute('aria-valuetext', `${gainToDb(value)} dB`);
  }

  /**
   * Signal level for a row's meter, 0–1. Kept separate from the fader: the
   * fader is where you set the level, the meter is what is coming out.
   */
  setMeterLevel(engineType, level) {
    const row = this.rows.get(engineType);
    if (!row) return;
    row.level.meterFill.style.height = `${Math.min(1, Math.max(0, level)) * 100}%`;
  }

  /* ---------------------------------------------------------------- *
   * View state
   * ---------------------------------------------------------------- */

  setMuted(engineType, muted) {
    const row = this.rows.get(engineType);
    if (!row) return;
    row.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    row.paramRow.classList.toggle('is-muted', muted);
    row.lane.classList.toggle('is-muted', muted);
  }

  setSoloed(engineType, soloed) {
    const row = this.rows.get(engineType);
    if (!row) return;
    row.soloBtn.setAttribute('aria-pressed', soloed ? 'true' : 'false');
    row.paramRow.classList.toggle('is-soloed', soloed);
    row.lane.classList.toggle('is-soloed', soloed);
  }

  /** Dim everything that a live solo is excluding. */
  setSoloDimming(anySoloed, soloedMap) {
    this.rows.forEach((row, engineType) => {
      const dimmed = anySoloed && !soloedMap[engineType];
      row.paramRow.classList.toggle('is-dimmed', dimmed);
      row.lane.classList.toggle('is-dimmed', dimmed);
    });
  }

  setFocus(engineType) {
    if (this.focusedEngine === engineType) return;
    this.focusedEngine = engineType;

    this.rows.forEach((row, type) => {
      const focused = type === engineType;
      row.paramRow.classList.toggle('is-focused', focused);
      row.lane.classList.toggle('is-focused', focused);
    });

    this.onFocusChange(engineType);
  }

  getFocus() {
    return this.focusedEngine;
  }

  /** Light the playing column. `-1` clears it. */
  setCurrentStep(stepIndex) {
    if (stepIndex === this.currentStep) return;

    if (this.currentStep >= 0 && this.playheadCells[this.currentStep]) {
      this.playheadCells[this.currentStep].classList.remove('is-on');
    }
    if (stepIndex >= 0 && this.playheadCells[stepIndex]) {
      this.playheadCells[stepIndex].classList.add('is-on');
    }

    this.currentStep = stepIndex;
  }

  setStepCount(count) {
    if (count === this.stepCount) return;
    this.stepCount = Math.min(MAX_STEPS, Math.max(STEPS_PER_BAR, count));

    this.buildStepHeader();
    this.rows.forEach((row, engineType) => {
      this.buildLaneSteps(engineType, row.lane, row.stepCells);
      this.syncSteps(engineType);
    });

    // Rebuilding drops the lit column; re-light it if the clock is running.
    const playing = this.currentStep;
    this.currentStep = -1;
    if (playing >= 0 && playing < this.stepCount) this.setCurrentStep(playing);

    this.stepsPanel.dataset.bars = String(Math.ceil(this.stepCount / STEPS_PER_BAR));
  }

  getParamsPanel() {
    return this.paramsPanel;
  }

  getStepsPanel() {
    return this.stepsPanel;
  }

  destroy() {
    window.removeEventListener('pointerup', this.endPaint);
    window.removeEventListener('pointercancel', this.endPaint);
    this.rows.clear();
    this.paramsPanel.remove();
    this.stepsPanel.remove();
  }
}
